import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { IComponentServiceProps} from '@/pages/dashboard/components/PageContainer/type';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import { FilterContext, RefreshContext } from '@/pages/dashboard/components/PageContainer/context';
import { stringToFunction } from '../../utils/stringToJson';
import { formatDataByRules, IFormatRule, IFormatRules } from '@/pages/dashboard/utils/dataFormatter';
import { normalizeFormatRules } from '@/pages/dashboard/utils/utils';

interface ITrackDataInfo {
  // 依赖列表
  list: Array<any>;
  // 依赖详细数据
  data: any;
};

interface IDataStateType {
  // 接口返回的数据
  data: any;
  // 接口向下传递的loading状态
  loading: boolean;
  // 接口真实的loading状态
  realLoading: boolean;
  // 错误类型
  errorType?: string;
  // 错误消息
  errorMsg?: string;
};

const InitData: IDataStateType = {
  data: null,
  loading: true,
  realLoading: true,
  errorType: '',
  errorMsg: ''
};

const Service = (props: IComponentServiceProps) => {
  const {
    data,
    dataKey,
    api,
    trackData = [],
    beforeRequest,
    formatData,
    interval,
    silentPolling,
    stopAutoRefreshWhen,
    children,
    refreshKey,
    filterVal: propsFilterVal,
    filterLoading: propsFilterLoading,
    rawData,
    formatRules,
  } = props;
  // 存取接口数据,包含状态
  const [iData, _setIData] = useState<IDataStateType>({ ...InitData });
  // 定时器
  const intervalTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  // 定时器执行次数
  const timerNumberRef = useRef<number>(0);
  // 是否点击了强制刷新
  const forceRefreshRef = useRef<boolean>(false);
  let { filterVal, filterLoading } = useContext(FilterContext);
  filterVal = (propsFilterVal !== undefined) ? propsFilterVal : filterVal;
  filterVal = Object.keys(filterVal ?? {}).length > 0 ? filterVal : data?.filter;
  filterLoading = (propsFilterLoading !== undefined) ? propsFilterLoading : filterLoading;

  // 点击刷新相关context
  const { forceRefresh, apiCollect, apiSuccess, apiFail } = useContext(RefreshContext);


  const setIData = (val = {}) => {
    _setIData({
      ...iData,
      ...val
    });
  };

  const normalizedFormatRules: IFormatRule[] = useMemo(() => {
    return normalizeFormatRules(formatRules);
  }, [formatRules]);

  useMemo(() => {
    forceRefreshRef.current = forceRefresh;
  }, [forceRefresh]);

  // todo: 静默刷新的时候，子service调用还是会有loading的问题，另外子service的刷新会执行两次
  const getApiData = useCallback((data: any, refresh = false, silentPolling = false,) => {
    let params = data;
    const filterVal = data?.filterVal;

    if (beforeRequest) {
      if (typeof beforeRequest === 'string') {
        params = stringToFunction(beforeRequest)(data);
      }
      if (typeof beforeRequest === 'function') {
        params = beforeRequest(data);
      }
    }
    // 请求拦截返回false，直接终止后续处理
    if (params === false) {
      return;
    }
    const loadingFlag = !refresh || !silentPolling;
    // 设置真实loading，以及向下传递的loading
    if (loadingFlag) {
      setIData({
        loading: true,
        realLoading: true
      });
    } else {
      setIData({
        realLoading: true
      });
    }

    let resultPromise = null;
    // api 类型处理，string和function
    if (typeof api === 'string') {
      resultPromise = dashboardRequest({
        url: api,
        method: 'post',
        data: params,
        rawData,
        ignoreRequestId: rawData, // 忽略requestId检查，避免多次请求时数据失效
      });
    } else if (typeof api === 'function') {
      resultPromise = api(params,data);
    }
    if (resultPromise) {
      resultPromise.then((resData: any) => {
        // 当使用 rawData: true 时，需要提取 data 字段
        let actualData = rawData ? resData?.data : resData;
        
        // 格式化数据，为性能考虑这会直接修改 actualData 对象
        if (normalizedFormatRules && Array.isArray(normalizedFormatRules) && normalizedFormatRules.length > 0) {
          formatDataByRules(actualData, normalizedFormatRules);
        }

        if (formatData) {
          if (typeof formatData === 'string') {
            actualData = stringToFunction(formatData)(actualData, filterVal);
          }
          if (typeof formatData === 'function') {
            actualData = formatData(actualData, filterVal);
          }
        }
        setIData({
          ...InitData,
          loading: false,
          realLoading: false,
          data: actualData,
          ...(resData?.req ? { req: resData.req } : {})
        });
        if (forceRefreshRef.current && refreshKey) {
          apiSuccess(refreshKey);
        }
      }).catch((err) => {
        // 设置错误信息
        setIData({
          ...InitData,
          loading: false,
          realLoading: false,
          errorType: 'apiError',
          errorMsg: err?.msg || 'api config error'
        });
        if (forceRefreshRef.current && refreshKey) {
          apiFail(refreshKey);
        }
      });
    } else {
      // 设置错误信息
      setIData({
        ...InitData,
        loading: false,
        errorType: 'configError',
        errorMsg: 'api config error'
      });
    }
  }, [iData]);

  // 组装data、loading、error 到子组件
  const childProps = useMemo(() => {
    return {
      data: {
        ...data,
        [dataKey]: iData,
        filter: filterVal,
      },
      filterLoading: propsFilterLoading
    };
  }, [data, iData,filterVal,propsFilterLoading]);

  // 设置service依赖数据
  const trackDataInfo: ITrackDataInfo = useMemo(() => {
    const dataMap: any = {};
    const list: Array<any> = [];
    trackData.forEach((key) => {
      const val = data[key];
      list.push(val);
      dataMap[key] = val;
    });
    return {
      list,
      data: dataMap
    };
  }, [data, trackData]);

  // 自动刷新功能处理，每次点击刷新的时候清除上一次的定时器，重新设置定时器
  const setDataFetching = () => {
    // 清除现有的定时器
    clearInterval(intervalTimerRef.current);
    // 如果设置了间隔，则启动定时器
    if (interval) {
      // 定时器计数归0
      timerNumberRef.current = 0;
      // 创建定时器
      intervalTimerRef.current = setInterval(() => {
        timerNumberRef.current += 1;
        if (stopAutoRefreshWhen?.(timerNumberRef.current)) {
          clearInterval(intervalTimerRef.current);
        } else {
          getApiData({ filterVal, ...trackDataInfo.data }, true, silentPolling);
        }
      }, interval);
    }
  };

  useEffect(() => {
    // 如果有点击刷新功能，则收集需要刷新的组件refreshKey供Refresh组件使用
    // loading 完之后再收集，不然刷新页面的时候可能会被refresh中useEffect的reset重置掉
    if (refreshKey) {
      apiCollect(refreshKey);
    }
  }, [refreshKey]);

  // 点击刷新请求数据
  useEffect(() => {
    if (!refreshKey || !forceRefresh || iData.realLoading) {
      return;
    }
    // 检查依赖项是否在加载中或有错误
    const dataLoading = trackDataInfo.list.some(val => val && (val.realLoading === true || val.errorMsg));
    if (dataLoading) {
      return;
    }
    getApiData({ filterVal, ...trackDataInfo.data }, false, false);
    setDataFetching();
  }, [forceRefresh]);

  // 初始化进入页面请求数据
  useEffect(() => {
    if (filterLoading) {
      return;
    }
    // 检查依赖项是否在加载中或有错误
    const dataLoading = trackDataInfo.list.some(val => val && (val.realLoading === true || val.errorMsg));
    if (dataLoading) {
      return;
    }
    getApiData({ filterVal, ...trackDataInfo.data });
    setDataFetching();
    return () => {
      clearInterval(intervalTimerRef.current);
    };
  }, [...trackDataInfo.list, filterVal, filterLoading]);

  return <>
    {
      React.Children.map(children, child => {
        return React.cloneElement(child, childProps);
      })
    }
  </>;
};

export default Service;
